/**
 * Mock Mapping Service
 * Simulates Multi-Agent Mapping System API calls with realistic delays.
 *
 * #100: die Szenario-Daten liefern `stage`/`rationale` auf `topMatch` und
 * `alternatives` direkt (der Kandidaten-Contract, den OmopAgentInfoPanel und
 * OmopCriticAgentPanel lesen) statt der Erzaehlung aus mockAiResponses.json,
 * die entfernt wurde. `agent3Output.allCandidates[].reason` bleibt bestehen --
 * OmopSuggestionsTable.js liest dieses Feld unveraendert und ist nicht Teil
 * dieses Tickets.
 */

import omopVocabulary from '../data/omopVocabulary.json';

const MODEL_NAME = 'gpt-4o-mini';

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const GENERATE_MAPPING_STAGES = [
  { name: 'agent1', duration: 2500, displayName: 'Agent 1: Descriptor', description: 'Generiere semantische Beschreibung aus Metadaten...' },
  { name: 'agent2', duration: 3000, displayName: 'Agent 2: Query Generator', description: 'Erstelle SQL-Queries für OMOP Vocabulary DB...' },
  { name: 'agent3', duration: 2500, displayName: 'Agent 3: Ranking Agent', description: 'Ranking und Scoring der Kandidaten...' }
];

const CRITIC_STAGE_DURATIONS = [2000, 2500, 1500];

const SCENARIOS = {
  highConfidence: {
    confidenceScore: 0.95,
    agentRanking: 1,
    totalCandidates: 8,
    candidates: [
      { conceptId: '3004249', conceptCode: '8480-6', conceptName: 'Systolic blood pressure', vocabulary: 'LOINC', domain: 'Measurement', score: 0.95, rationale: 'Exact match for systolic blood pressure measurement, standard LOINC code' },
      { conceptId: '4152194', conceptCode: '271649006', conceptName: 'Systolic blood pressure (observable entity)', vocabulary: 'SNOMED', domain: 'Measurement', score: 0.87, rationale: 'SNOMED equivalent, slightly lower due to less specific clinical context' }
    ]
  },
  mediumConfidence: {
    confidenceScore: 0.78,
    agentRanking: 1,
    totalCandidates: 15,
    candidates: [
      { conceptId: '3000494', conceptCode: '2524-7', conceptName: 'Lactate [Moles/volume] in Blood', vocabulary: 'LOINC', domain: 'Measurement', score: 0.78, rationale: 'Best match for lactate measurement in blood' },
      { conceptId: '3019550', conceptCode: '2519-7', conceptName: 'Lactate [Moles/volume] in Serum or Plasma', vocabulary: 'LOINC', domain: 'Measurement', score: 0.72, rationale: 'Alternative specimen type (serum/plasma vs blood)' }
    ]
  },
  lowConfidence: {
    confidenceScore: 0.52,
    agentRanking: 1,
    totalCandidates: 3,
    candidates: [
      { conceptId: '3000000', conceptCode: '99999-9', conceptName: 'Generic measurement', vocabulary: 'LOINC', domain: 'Measurement', score: 0.52, rationale: 'Low confidence match, manual review recommended' }
    ]
  },
  noResults: {
    confidenceScore: 0,
    agentRanking: null,
    totalCandidates: 0,
    candidates: []
  }
};

const toCandidateFields = (candidate, extra = {}) => ({
  conceptId: candidate.conceptId,
  conceptCode: candidate.conceptCode,
  conceptName: candidate.conceptName,
  vocabulary: candidate.vocabulary,
  domain: candidate.domain,
  stage: 'agent3',
  rationale: candidate.rationale,
  ...extra
});

/**
 * Stage callback for progress updates during AI mapping generation
 * @callback StageCallback
 * @param {string} stageName - Name of the current stage
 * @param {string} status - Status of the stage (running, completed, error)
 * @param {object} data - Stage-specific data
 */

/**
 * Generate AI mapping for a parameter
 * Simulates the Multi-Agent System pipeline (Agent 1 → 2 → 3)
 *
 * @param {object} parameter - Parameter object to generate mapping for
 * @param {StageCallback} onStageUpdate - Optional callback for stage progress updates
 * @returns {Promise<object>} - Generated mapping result with aiMetadata
 */
export const generateAiMapping = async (parameter, onStageUpdate = null) => {
  try {
    let scenarioKey = 'mediumConfidence';
    if (parameter.tags && parameter.tags.length > 3 && parameter.description) {
      scenarioKey = 'highConfidence';
    } else if (!parameter.description || parameter.tags?.length < 2) {
      scenarioKey = 'lowConfidence';
    }

    const scenario = SCENARIOS[scenarioKey];

    for (const stage of GENERATE_MAPPING_STAGES) {
      if (onStageUpdate) {
        onStageUpdate(stage.name, 'running', { displayName: stage.displayName, description: stage.description });
      }
      await delay(stage.duration);
      if (onStageUpdate) {
        onStageUpdate(stage.name, 'completed', { displayName: stage.displayName });
      }
    }

    const [topCandidate, ...alternativeCandidates] = scenario.candidates;

    return {
      status: 'success',
      omopMapping: {
        status: 'mapped',
        topMatch: topCandidate ? toCandidateFields(topCandidate) : null,
        alternatives: alternativeCandidates.slice(0, 3).map((candidate) =>
          toCandidateFields(candidate, { similarityScore: candidate.score })
        ),
        aiMetadata: {
          model: MODEL_NAME,
          timestamp: new Date().toISOString(),
          confidenceScore: scenario.confidenceScore,
          agentRanking: scenario.agentRanking,
          totalCandidates: scenario.totalCandidates,
          agent3Output: {
            allCandidates: scenario.candidates.map((candidate, idx) => ({
              conceptId: candidate.conceptId,
              conceptCode: candidate.conceptCode,
              conceptName: candidate.conceptName,
              vocabulary: candidate.vocabulary,
              domain: candidate.domain,
              score: candidate.score,
              rank: idx + 1,
              reason: candidate.rationale
            }))
          }
        },
        validationHistory: [],
        criticHistory: []
      }
    };
  } catch (error) {
    return {
      status: 'error',
      error: { message: 'Interner Serverfehler bei Agent-Ausführung' }
    };
  }
};

/**
 * Submit feedback to Critic Agent for iterative refinement
 *
 * @param {number} parameterId - Parameter ID
 * @param {string} feedback - User feedback text
 * @param {object} currentMapping - Current mapping data
 * @returns {Promise<object>} - Updated mapping with new ranking
 */
export const submitCriticFeedback = async (parameterId, feedback, currentMapping) => {
  try {
    for (const duration of CRITIC_STAGE_DURATIONS) {
      await delay(duration);
    }

    const alternatives = currentMapping.alternatives || [];
    if (alternatives.length === 0) {
      return {
        status: 'error',
        error: { message: 'No alternatives available for re-ranking' }
      };
    }

    const promoted = alternatives[0];
    const previousTopMatch = {
      conceptId: currentMapping.topMatch.conceptId,
      conceptCode: currentMapping.topMatch.conceptCode,
      conceptName: currentMapping.topMatch.conceptName,
      confidenceScore: currentMapping.aiMetadata?.confidenceScore
    };
    const newTopMatch = {
      conceptId: promoted.conceptId,
      conceptCode: promoted.conceptCode,
      conceptName: promoted.conceptName,
      vocabulary: promoted.vocabulary,
      confidenceScore: promoted.similarityScore || 0.9,
      stage: 'critic',
      rationale: 'Re-ranked based on user feedback regarding specific criteria'
    };

    const iteration = {
      iteration: (currentMapping.criticHistory?.length || 0) + 1,
      user: 'Current User', // TODO: Get from auth context
      model: MODEL_NAME,
      feedback,
      timestamp: new Date().toISOString(),
      newTopMatch,
      previousTopMatch
    };

    const updatedMapping = {
      ...currentMapping,
      topMatch: {
        conceptId: newTopMatch.conceptId,
        conceptCode: newTopMatch.conceptCode,
        conceptName: newTopMatch.conceptName,
        vocabulary: newTopMatch.vocabulary,
        stage: newTopMatch.stage,
        rationale: newTopMatch.rationale
      },
      // topMatch traegt jetzt stage: 'critic' -- aiMetadata.model/timestamp muessen
      // dieselbe Iteration ausweisen, sonst zeigt OmopAgentInfoPanel fuer einen
      // Critic-Vorschlag das Modell/den Zeitpunkt des Vorlaufs (#100 Rueckmeldung
      // Runde 5, offener Blocker AK4).
      aiMetadata: {
        ...currentMapping.aiMetadata,
        model: iteration.model,
        timestamp: iteration.timestamp,
        confidenceScore: newTopMatch.confidenceScore
      },
      criticHistory: [...(currentMapping.criticHistory || []), iteration]
    };

    return {
      status: 'success',
      omopMapping: updatedMapping
    };
  } catch (error) {
    return {
      status: 'error',
      error: { message: 'Interner Serverfehler bei Agent-Ausführung' }
    };
  }
};

/**
 * Search OMOP vocabulary
 *
 * @param {string} query - Search query
 * @param {object} filters - Filter options (vocabulary, domain, etc.)
 * @param {number} page - Page number (1-indexed)
 * @returns {Promise<object>} - Search results
 */
export const searchVocabulary = async (query, filters = {}, page = 1) => {
  const resultsPerPage = 20;

  await delay(800);

  let results = omopVocabulary.concepts.filter(concept => {
    const matchesQuery = !query ||
      concept.conceptName.toLowerCase().includes(query.toLowerCase()) ||
      concept.conceptSynonyms?.some(syn => syn.toLowerCase().includes(query.toLowerCase()));

    const matchesVocabulary = !filters.vocabulary || filters.vocabulary === 'all' ||
      concept.vocabulary === filters.vocabulary;

    const matchesDomain = !filters.domain || filters.domain === 'all' ||
      concept.domain === filters.domain;

    return matchesQuery && matchesVocabulary && matchesDomain;
  });

  const totalResults = results.length;
  const totalPages = Math.ceil(totalResults / resultsPerPage);
  const startIdx = (page - 1) * resultsPerPage;
  const endIdx = startIdx + resultsPerPage;
  const paginatedResults = results.slice(startIdx, endIdx);

  return {
    status: 'success',
    results: paginatedResults,
    pagination: {
      currentPage: page,
      totalPages: totalPages,
      totalResults: totalResults,
      resultsPerPage: resultsPerPage
    }
  };
};

const mockMappingService = {
  generateAiMapping,
  submitCriticFeedback,
  searchVocabulary
};

export default mockMappingService;
